import React, { useState, useRef, useEffect } from 'react';
import { useNewPreemieContext } from './NewPreemieContext';

interface OpeningConditionsProps {
  onUpdate?: () => void;
}

const OpeningConditions: React.FC<OpeningConditionsProps> = ({ onUpdate }) => {
  const {
    anamnesis,
    birthReason,
    gestationalAgeWeeks,
    gestationalAgeDays,
    weightAssessment,
    gender,
    updateContext
  } = useNewPreemieContext();

  const [localAnamnesis, setLocalAnamnesis] = useState(anamnesis);
  const [localBirthReason, setLocalBirthReason] = useState(birthReason);
  const [localGestationalAgeWeeks, setLocalGestationalAgeWeeks] = useState(
    gestationalAgeWeeks?.toString() || ''
  );
  const [localGestationalAgeDays, setLocalGestationalAgeDays] = useState(
    gestationalAgeDays !== null && gestationalAgeDays !== undefined ? gestationalAgeDays.toString() : '0'
  );
  const [localWeightAssessment, setLocalWeightAssessment] = useState(
    weightAssessment?.toString() || ''
  );
  const [localGender, setLocalGender] = useState<'זכר' | 'נקבה' | null>(gender);
  const [gestationalWeekError, setGestationalWeekError] = useState<string | null>(null);
  const weekValidationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (weekValidationTimerRef.current) {
        clearTimeout(weekValidationTimerRef.current);
      }
    };
  }, []);

  const validateGestationalWeeks = (value: string): boolean => {
    if (!value) return true; // Empty is valid (will be caught on submit)
    const weeks = parseInt(value, 10);
    return !isNaN(weeks) && weeks >= 20 && weeks <= 43;
  };

  const handleGestationalWeeksChange = (value: string) => {
    setLocalGestationalAgeWeeks(value);

    // Clear any existing timer
    if (weekValidationTimerRef.current) {
      clearTimeout(weekValidationTimerRef.current);
    }

    // Set validation timer for 1 second
    if (value && !validateGestationalWeeks(value)) {
      weekValidationTimerRef.current = setTimeout(() => {
        setGestationalWeekError('שבוע הריון יכול להיות בין 20 ל 43');
      }, 1000);
    } else {
      setGestationalWeekError(null);
    }
  };

  const handleGestationalWeeksBlur = () => {
    // Clear timer and validate immediately on blur
    if (weekValidationTimerRef.current) {
      clearTimeout(weekValidationTimerRef.current);
    }

    if (localGestationalAgeWeeks && !validateGestationalWeeks(localGestationalAgeWeeks)) {
      setGestationalWeekError('שבוע הריון יכול להיות בין 20 ל 43');
    } else {
      setGestationalWeekError(null);
    }
  };

  const handleGestationalDaysChange = (value: string) => {
    // Clamp days to 0-6
    if (!value) {
      setLocalGestationalAgeDays('0');
      return;
    }
    const days = parseInt(value, 10);
    if (isNaN(days)) {
      setLocalGestationalAgeDays('0');
    } else if (days < 0) {
      setLocalGestationalAgeDays('0');
    } else if (days > 6) {
      setLocalGestationalAgeDays('6');
    } else {
      setLocalGestationalAgeDays(days.toString());
    }
  };

  const handleUpdate = () => {
    // Validate gestational weeks before update
    if (localGestationalAgeWeeks && !validateGestationalWeeks(localGestationalAgeWeeks)) {
      setGestationalWeekError('שבוע הריון יכול להיות בין 20 ל 43');
      return;
    }

    const gestationalAgeWeeksValue = localGestationalAgeWeeks ? parseInt(localGestationalAgeWeeks, 10) : null;
    const gestationalAgeDaysValue = parseInt(localGestationalAgeDays, 10) || 0;
    const weightAssessmentValue = localWeightAssessment ? parseInt(localWeightAssessment, 10) : null;

    updateContext(
      localAnamnesis,
      localBirthReason,
      gestationalAgeWeeksValue,
      gestationalAgeDaysValue,
      weightAssessmentValue,
      localGender
    );

    onUpdate?.();
  };

  const handleClear = () => {
    setLocalAnamnesis('');
    setLocalBirthReason('');
    setLocalGestationalAgeWeeks('');
    setLocalGestationalAgeDays('0');
    setLocalWeightAssessment('');
    setLocalGender(null);
    setGestationalWeekError(null);
  };

  return (
    <div className="opening-conditions-container" dir="rtl">
      <form className="opening-conditions-form">
        <div className="row form-group">
          <div className="col-auto col-auto-text-cols">אנמנזה</div>
          <div className="col input-col">
            <textarea
              className="form-control"
              rows={3}
              value={localAnamnesis}
              onChange={(e) => setLocalAnamnesis(e.target.value)}
              placeholder="אנמנזה"
            />
          </div>
        </div>

        <div className="row form-group">
          <div className="col-auto col-auto-text-cols">סיבה ללידה מוקדמת</div>
          <div className="col input-col">
            <input
              type="text"
              className="form-control input-field"
              value={localBirthReason}
              onChange={(e) => setLocalBirthReason(e.target.value)}
              placeholder="סיבה"
            />
          </div>
        </div>

        <div className="row form-group">
          <div className="col-auto col-auto-text-cols">שבוע הריון</div>
          <div className="col input-col">
            <div className="gestational-age-fields">
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-control input-field"
                value={localGestationalAgeWeeks}
                onChange={(e) => handleGestationalWeeksChange(e.target.value)}
                onBlur={handleGestationalWeeksBlur}
                onFocus={(e) => e.target.select()}
                placeholder="שבועות"
                min="20"
                max="43"
              />
              <div className="gestational-age-days-wrapper">
                <span className="gestational-age-plus">+</span>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="form-control input-field"
                  value={localGestationalAgeDays}
                  onChange={(e) => handleGestationalDaysChange(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  placeholder="ימים"
                  min="0"
                  max="6"
                  step="1"
                />
                <span className="gestational-age-days-label">ימים</span>
              </div>
            </div>
            {gestationalWeekError && (
              <div style={{ color: '#D32F2F', fontSize: '0.85rem', marginTop: '4px' }}>
                {gestationalWeekError}
              </div>
            )}
          </div>
        </div>

        <div className="row form-group">
          <div className="col-auto col-auto-text-cols">הערכת משקל (גרם)</div>
          <div className="col input-col">
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control input-field"
              value={localWeightAssessment}
              onChange={(e) => setLocalWeightAssessment(e.target.value)}
              onFocus={(e) => e.target.select()}
              placeholder="משקל בגרמים"
            />
          </div>
        </div>

        <div className="row form-group">
          <div className="col-auto col-auto-text-cols">מין</div>
          <div className="col input-col">
            <div className="radio-options">
              <label className="radio-label">
                <input
                  type="radio"
                  name="gender"
                  value="זכר"
                  checked={localGender === 'זכר'}
                  onChange={(e) => setLocalGender(e.target.value as 'זכר')}
                />
                זכר
              </label>

              <label className="radio-label">
                <input
                  type="radio"
                  name="gender"
                  value="נקבה"
                  checked={localGender === 'נקבה'}
                  onChange={(e) => setLocalGender(e.target.value as 'נקבה')}
                />
                נקבה
              </label>
            </div>
          </div>
        </div>

        <div className="row form-group">
          <div className="col text-center">
            <button type="button" className="btn btn-update" onClick={handleUpdate}>
              עדכן
            </button>
            <button type="button" className="btn btn-reset" onClick={handleClear}>
              נקה
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OpeningConditions;